import { json, type LoaderFunctionArgs, type MetaFunction, redirect } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import {
	CloudArrowUpIcon,
	CreditCardIcon,
	FingerPrintIcon,
	LockClosedIcon,
	ServerStackIcon,
	ShieldCheckIcon,
	StarIcon,
} from '@heroicons/react/20/solid'
import React, { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { getDefaultHomePath } from '#app/utils/home-page.server.ts'
import { getUserId } from '#app/utils/session.server'
import { useTranslation } from 'react-i18next'
import AnchorButton from '#app/components/buttons/anchor-button.tsx'

export const meta: MetaFunction = () => [{ title: 'refundr' }]

const features = [
	{
		name: 'marketing.index.features.0.name',
		description: 'marketing.index.features.0.description',
		icon: CloudArrowUpIcon,
	},
	{
		name: 'marketing.index.features.1.name',
		description: 'marketing.index.features.1.description',
		icon: LockClosedIcon,
	},
	{
		name: 'marketing.index.features.2.name',
		description: 'marketing.index.features.2.description',
		icon: LockClosedIcon,
	},
]

const securityFeatures = [
	{
		name: 'marketing.index.security.features.0.name',
		description: 'marketing.index.security.features.0.description',
		icon: ShieldCheckIcon,
	},
	{
		name: 'marketing.index.security.features.1.name',
		description: 'marketing.index.security.features.1.description',
		icon: FingerPrintIcon,
	},
	{
		name: 'marketing.index.security.features.2.name',
		description: 'marketing.index.security.features.2.description',
		icon: ServerStackIcon,
	},
	{
		name: 'marketing.index.security.features.3.name',
		description: 'marketing.index.security.features.3.description',
		icon: CreditCardIcon,
	},
]

const faqs = [
	{
		id: 1,
		question: 'marketing.index.faqs.0.question',
		answer: 'marketing.index.faqs.0.answer',
	},
	{
		id: 2,
		question: 'marketing.index.faqs.1.question',
		answer: 'marketing.index.faqs.1.answer',
	},
	{
		id: 3,
		question: 'marketing.index.faqs.2.question',
		answer: 'marketing.index.faqs.2.answer',
	},
	{
		id: 4,
		question: 'marketing.index.faqs.3.question',
		answer: 'marketing.index.faqs.3.answer',
	},
	{
		id: 5,
		question: 'marketing.index.faqs.4.question',
		answer: 'marketing.index.faqs.4.answer',
	},
]

export async function loader({ request }: LoaderFunctionArgs) {
	const userId = await getUserId(request)
	if (userId) {
		return redirect(getDefaultHomePath(request))
	}
	return json({ userId })
}

export default function Index() {
	const data = useLoaderData<typeof loader>()
	const boxRef = useRef<HTMLDivElement>(null)
	const { t } = useTranslation()

	useEffect(() => {
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: boxRef.current,
				start: 'top center',
				end: 'bottom center',
				// @ts-ignore
				scrubber: true,
				markers: false,
			},
		})

		tl.fromTo(boxRef.current, { x: '100vh', opacity: 0 }, { x: '0', opacity: 1, duration: 1 })
	}, [])

	return (
		<div>
			{/* Hero — light by default; original dark navy in dark mode */}
			<div className="relative overflow-hidden bg-[hsl(40_20%_98%)] dark:bg-[#0D0D0D]">
				<div
					aria-hidden="true"
					className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(180,160,130,0.18),_transparent_55%),radial-gradient(ellipse_at_bottom_left,_rgba(200,190,175,0.22),_transparent_50%)] dark:hidden"
				/>
				<div className="relative isolate">
					<div className="mx-auto max-w-7xl px-3 py-16 sm:py-20 lg:grid lg:max-w-[84rem] lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] lg:items-center lg:gap-x-8 lg:px-8 lg:py-16 xl:grid-cols-[minmax(0,30rem)_minmax(0,1fr)] xl:gap-x-10">
						<div className="mx-auto max-w-xl lg:mx-0 lg:max-w-none">
							<div className="pb-4 text-neutral-900 dark:text-white">
								<div className="py-3">
									<div className="font-simpletfb text-6xl tracking-tight text-neutral-900 sm:text-7xl dark:text-white">
										refundr
									</div>
									<div className="mt-2 font-proxima text-lg text-neutral-600 sm:text-xl dark:font-mono dark:text-white">
										{t('marketing.index.title')}
									</div>
								</div>
								<div className="mb-6 mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
									<Link
										to={data.userId ? '/returns' : '/login'}
										className="rounded-md bg-indigo-600 px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
									>
										{data.userId ? t('marketing.index.button.taxReturn') : t('marketing.index.button.logIn')}
									</Link>
									{!data.userId && (
										<Link
											to="/signup"
											className="rounded-md bg-indigo-600 px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
										>
											{t('marketing.index.button.signUp')}
										</Link>
									)}
								</div>
								<div className="font-proxima text-lg text-neutral-800 sm:text-xl dark:font-mono dark:text-white">
									{t('marketing.index.subtitle1')}
								</div>
								<div className="pt-2 font-proxima text-base text-neutral-600 sm:text-lg dark:font-mono dark:text-white">
									{t('marketing.index.subtitle2')}
								</div>
							</div>
							<p className="font-proxima text-base text-neutral-600 sm:text-lg dark:font-mono dark:text-gray-400">
								{t('marketing.index.description1')}
							</p>
							<p className="mt-3 font-proxima text-base text-neutral-600 sm:text-lg dark:font-mono dark:text-gray-400">
								{t('marketing.index.description2')}
							</p>
							<div className="mt-5">
								<AnchorButton href="https://dashboard.refundr.ca">{t('marketing.index.taxProfessional')}</AnchorButton>
							</div>
						</div>
						<div
							ref={boxRef}
							className="mx-auto mt-10 w-full min-w-0 max-w-3xl sm:mt-12 lg:mt-0 lg:max-w-[48rem] xl:max-w-[52rem]"
						>
							<img
								src="/img/hero.png?v=3"
								alt="Client and tax professional collaborating on refundr"
								width={1172}
								height={937}
								className="h-auto w-full object-contain"
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="overflow-hidden bg-white py-24 sm:py-32 dark:bg-white">
				<div className="mx-auto max-w-7xl px-6 lg:px-8">
					<div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
						<div className="lg:pr-8 lg:pt-4">
							<div className="lg:max-w-lg">
								<h2 className="text-base/7 font-semibold text-indigo-600">
									{t('marketing.index.featureHeader')}
								</h2>
								<p className="mt-2 text-pretty font-proxima text-4xl font-semibold tracking-tight text-neutral-900 sm:text-5xl">
									{t('marketing.index.featureTitle')}
								</p>
								<p className="mt-6 font-proxima text-lg/8 text-neutral-600">{t('marketing.index.featureDescription')}</p>
								<dl className="mt-10 max-w-xl space-y-8 font-proxima text-base/7 text-neutral-600 lg:max-w-none">
									{features.map(feature => (
										<div key={feature.name} className="relative pl-9">
											<dt className="inline font-semibold text-neutral-900">
												<feature.icon
													aria-hidden="true"
													className="absolute left-1 top-1 size-5 text-indigo-600"
												/>
												{t(feature.name)}
											</dt>{' '}
											<dd className="inline">{t(feature.description)}</dd>
										</div>
									))}
								</dl>
							</div>
						</div>
						<img
							alt="Product screenshot"
							src="/img/home/returns.png"
							width={2432}
							height={1442}
							className="w-[48rem] max-w-none rounded-xl shadow-xl ring-1 ring-black/5 sm:w-[57rem] md:-ml-4 lg:-ml-0"
						/>
					</div>
				</div>
			</div>

			<div className="bg-[hsl(40_12%_96%)] py-24 sm:py-32 dark:bg-gray-50">
				<div className="mx-auto max-w-7xl px-6 lg:px-8">
					<div className="mx-auto max-w-2xl lg:text-center">
						<h2 className="text-base/7 font-semibold text-indigo-600">
							{t('marketing.index.security.header')}
						</h2>
						<p className="mt-2 text-pretty font-proxima text-4xl font-semibold tracking-tight text-neutral-900 sm:text-5xl">
							{t('marketing.index.security.title')}
						</p>
						<p className="mt-6 font-proxima text-lg/8 text-neutral-600">{t('marketing.index.security.description')}</p>
					</div>
					<div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
						<dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
							{securityFeatures.map(feature => (
								<div key={feature.name} className="flex flex-col">
									<dt className="flex items-center gap-x-3 font-proxima text-base/7 font-semibold text-neutral-900">
										<feature.icon
											aria-hidden="true"
											className="size-5 flex-none text-indigo-600"
										/>
										{t(feature.name)}
									</dt>
									<dd className="mt-4 flex flex-auto flex-col font-proxima text-base/7 text-neutral-600">
										<p className="flex-auto">{t(feature.description)}</p>
									</dd>
								</div>
							))}
						</dl>
					</div>
				</div>
			</div>

			<div className="bg-white py-24 sm:py-32 dark:bg-[#0D0D0D]">
				<div className="mx-auto max-w-2xl px-6 lg:max-w-7xl lg:px-8">
					<h2 className="text-center text-base/7 font-semibold text-indigo-600 dark:text-indigo-400">
						{t('marketing.index.reviewHeader')}
					</h2>
					<p className="mx-auto mt-2 max-w-lg text-balance text-center font-proxima text-4xl font-semibold tracking-tight text-neutral-900 sm:text-5xl dark:text-white">
						{t('marketing.index.reviewDescription')}
					</p>
					<div className="mt-10 grid gap-4 sm:mt-16 lg:grid-cols-3 lg:grid-rows-1">
						<div className="relative">
							<div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] bg-[hsl(40_20%_98%)] lg:rounded-l-[calc(2rem+1px)] dark:bg-white">
								<section className="px-6 py-10 sm:py-20 lg:px-8">
									<figure className="mx-auto max-w-2xl">
										<p className="sr-only">5 out of 5 stars</p>
										<div className="flex gap-x-1 text-neutral-900 dark:text-indigo-600">
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
										</div>
										<blockquote className="mt-10 font-proxima text-xl/8 font-semibold tracking-tight text-neutral-900 sm:text-2xl/9">
											<p>{t('marketing.index.reviews.0.reviewP1')}</p>
											<p>{t('marketing.index.reviews.0.reviewP2')}</p>
										</blockquote>
										<figcaption className="mt-10 flex items-center gap-x-6">
											<img
												alt=""
												src="/img/home/profile/michael-saruna.jpeg"
												className="size-12 rounded-full bg-neutral-100"
											/>
											<div className="font-proxima text-sm/6">
												<div className="font-semibold text-neutral-900">{t('marketing.index.reviews.0.name')}</div>
												<div className="mt-0.5 text-neutral-600">{t('marketing.index.reviews.0.role')}</div>
											</div>
										</figcaption>
									</figure>
								</section>
							</div>
							<div className="pointer-events-none absolute inset-px rounded-lg shadow-sm ring-1 ring-black/5 lg:rounded-l-[2rem]"></div>
						</div>
						<div className="relative max-lg:row-start-1">
							<div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] bg-[hsl(40_20%_98%)] max-lg:rounded-t-[calc(2rem+1px)] dark:bg-white">
								<section className="px-6 py-10 sm:py-20 lg:px-8">
									<figure className="mx-auto max-w-2xl">
										<p className="sr-only">5 out of 5 stars</p>
										<div className="flex gap-x-1 text-neutral-900 dark:text-indigo-600">
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
										</div>
										<blockquote className="mt-10 font-proxima text-xl/8 font-semibold tracking-tight text-neutral-900 sm:text-2xl/9">
											<p>{t('marketing.index.reviews.1.reviewP1')}</p>
											<p>{t('marketing.index.reviews.1.reviewP2')}</p>
											<p>{t('marketing.index.reviews.1.reviewP3')}</p>
										</blockquote>
										<figcaption className="mt-10 flex items-center gap-x-6">
											<img
												alt=""
												src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=1024&h=1024&q=80"
												className="size-12 rounded-full bg-neutral-100"
											/>
											<div className="font-proxima text-sm/6">
												<div className="font-semibold text-neutral-900">{t('marketing.index.reviews.1.name')}</div>
												<div className="mt-0.5 text-neutral-600">{t('marketing.index.reviews.1.role')}</div>
											</div>
										</figcaption>
									</figure>
								</section>
							</div>
							<div className="pointer-events-none absolute inset-px rounded-lg shadow-sm ring-1 ring-black/5 max-lg:rounded-t-[2rem]"></div>
						</div>
						<div className="relative">
							<div className="absolute inset-px rounded-lg bg-[hsl(40_20%_98%)] max-lg:rounded-b-[2rem] lg:rounded-r-[2rem] dark:bg-white"></div>
							<div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-b-[calc(2rem+1px)] lg:rounded-r-[calc(2rem+1px)]">
								<section className="px-6 py-10 sm:py-20 lg:px-8">
									<figure className="mx-auto max-w-2xl">
										<p className="sr-only">5 out of 5 stars</p>
										<div className="flex gap-x-1 text-neutral-900 dark:text-indigo-600">
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
											<StarIcon aria-hidden="true" className="size-5 flex-none" />
										</div>
										<blockquote className="mt-10 font-proxima text-xl/8 font-semibold tracking-tight text-neutral-900 sm:text-2xl/9">
											{t('marketing.index.reviews.2.review')}
										</blockquote>
										<figcaption className="mt-10 flex items-center gap-x-6">
											<img
												alt=""
												src="/img/home/profile/heather-hartley.jpeg"
												className="size-12 rounded-full bg-neutral-100"
											/>
											<div className="font-proxima text-sm/6">
												<div className="font-semibold text-neutral-900">{t('marketing.index.reviews.2.name')}</div>
												<div className="mt-0.5 text-neutral-600">{t('marketing.index.reviews.2.role')}</div>
											</div>
										</figcaption>
									</figure>
								</section>
							</div>
							<div className="pointer-events-none absolute inset-px rounded-lg shadow-sm ring-1 ring-black/5 max-lg:rounded-b-[2rem] lg:rounded-r-[2rem]"></div>
						</div>
					</div>
				</div>
			</div>

			<div className="bg-white">
				<div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40">
					<div className="mx-auto max-w-4xl">
						<h2 className="font-proxima text-4xl font-semibold tracking-tight text-neutral-900 sm:text-5xl">
							{t('marketing.index.faqHeader')}
						</h2>
						<dl className="mt-16 divide-y divide-neutral-900/10">
							{faqs.map(faq => (
								<Disclosure key={faq.question} as="div" className="py-6 first:pt-0 last:pb-0">
									<dt>
										<DisclosureButton className="group flex w-full items-start justify-between text-left text-neutral-900">
											<span className="font-proxima text-base/7 font-semibold">{t(faq.question)}</span>
											<span className="ml-6 flex h-7 items-center">
												<PlusIcon aria-hidden="true" className="size-6 group-data-[open]:hidden" />
												<MinusIcon aria-hidden="true" className="size-6 group-[&:not([data-open])]:hidden" />
											</span>
										</DisclosureButton>
									</dt>
									<DisclosurePanel as="dd" className="mt-2 pr-12">
										<p className="font-proxima text-base/7 text-neutral-600">{t(faq.answer)}</p>
									</DisclosurePanel>
								</Disclosure>
							))}
						</dl>
					</div>
				</div>
			</div>

			{/* Closing band — soft light; original dark closing band in dark mode */}
			<div className="relative bg-[hsl(40_12%_96%)] dark:bg-gray-900">
				<div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:flex lg:items-center lg:justify-between lg:px-8 lg:py-28">
					<div className="max-w-2xl">
						<h2 className="text-base/7 font-semibold text-indigo-600 dark:text-indigo-400">
							{t('marketing.index.section.header')}
						</h2>
						<p className="mt-2 font-proxima text-4xl font-semibold tracking-tight text-neutral-900 sm:text-5xl dark:text-white">
							{t('marketing.index.section.title')}
						</p>
						<p className="mt-6 font-proxima text-base/7 text-neutral-600 dark:text-gray-300">
							{t('marketing.index.section.description')}
						</p>
						<div className="mt-8">
							<Link
								to={data.userId ? '/returns' : '/login'}
								className="inline-flex rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-white/10 dark:hover:bg-white/20 dark:focus-visible:outline-white"
							>
								{t('marketing.index.section.buttonText')}
							</Link>
						</div>
					</div>
					<div className="mt-10 lg:mt-0 lg:w-1/3">
						<img
							alt=""
							src="/img/home/canada-flag.jpg"
							className="aspect-[4/3] w-full rounded-2xl object-cover shadow-sm ring-1 ring-black/5"
						/>
					</div>
				</div>
			</div>
		</div>
	)
}
